import { useState, useCallback } from 'react';
import { message } from 'antd';
import { d506Api, PendingItem, PendingResponse, ApproveResponse, ApprovalFilter } from '../../../services/d506Api';

export interface UseD506ApprovalReturn {
  // State
  loading: boolean;
  approving: boolean;
  pendingItems: PendingItem[];
  total: number;
  selectedKeys: string[];
  filter: ApprovalFilter;

  // Actions
  setFilter: (f: ApprovalFilter) => void;
  setSelectedKeys: (keys: string[]) => void;
  fetchPending: () => Promise<void>;
  batchApprove: () => Promise<ApproveResponse | null>;
}

export function useD506Approval(): UseD506ApprovalReturn {
  const [loading, setLoading] = useState(false);
  const [approving, setApproving] = useState(false);
  const [pendingItems, setPendingItems] = useState<PendingItem[]>([]);
  const [total, setTotal] = useState(0);
  const [selectedKeys, setSelectedKeys] = useState<string[]>([]);
  const [filter, setFilter] = useState<ApprovalFilter>({ docStart: '', docEnd: '' });

  const fetchPending = useCallback(async () => {
    if (!filter.docStart || !filter.docEnd) {
      message.warning('請輸入退出單號起始和結束號碼');
      return;
    }
    setLoading(true);
    setSelectedKeys([]);
    try {
      const resp: PendingResponse = await d506Api.getPending(filter);
      setPendingItems(resp.items || []);
      setTotal(resp.total || 0);
    } catch (err: unknown) {
      const msg = (err as any)?.response?.data?.error || (err instanceof Error ? err.message : '查詢失敗');
      message.error(msg);
    } finally {
      setLoading(false);
    }
  }, [filter]);

  const batchApprove = useCallback(async (): Promise<ApproveResponse | null> => {
    if (selectedKeys.length === 0) {
      message.warning('請至少勾選一筆退出單進行核准');
      return null;
    }
    setApproving(true);
    try {
      const resp = await d506Api.approve(selectedKeys);
      message.success(resp.message);
      // Refresh list after approval
      await fetchPending();
      return resp;
    } catch (err: unknown) {
      const msg = (err as any)?.response?.data?.error || (err instanceof Error ? err.message : '核准失敗');
      message.error(msg);
      return null;
    } finally {
      setApproving(false);
    }
  }, [selectedKeys, fetchPending]);

  return {
    loading,
    approving,
    pendingItems,
    total,
    selectedKeys,
    filter,
    setFilter,
    setSelectedKeys,
    fetchPending,
    batchApprove,
  };
}
